import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Folder, Image as ImageIcon, Video } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { MediaTile, Lightbox } from "@/components/GalleryMedia";

function FolderCard({ f, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`gallery-folder-${f.project_id}`}
      className="group block w-full text-left overflow-hidden rounded-2xl bg-white ring-1 ring-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-300">
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {f.cover && <img src={f.cover} alt={f.project_name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-void/80 via-transparent to-transparent" />
        <span className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-xl bg-white/90 text-navy"><Folder className="h-5 w-5" /></span>
        <div className="absolute bottom-3 left-4 flex gap-2 text-xs font-semibold text-white">
          <span className="inline-flex items-center gap-1 rounded-full bg-black/40 px-2.5 py-1 backdrop-blur"><ImageIcon className="h-3.5 w-3.5" /> {f.photos}</span>
          {f.videos > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-black/40 px-2.5 py-1 backdrop-blur"><Video className="h-3.5 w-3.5" /> {f.videos}</span>}
        </div>
      </div>
      <div className="p-4 sm:p-5">
        <h3 className="font-display text-lg font-semibold text-slate-900 group-hover:text-navy">{f.project_name}</h3>
        <p className="mt-0.5 text-sm text-slate-500 truncate">{f.location}</p>
      </div>
    </button>
  );
}

export default function Gallery() {
  const [params, setParams] = useSearchParams();
  const folderId = params.get("project");
  const [folders, setFolders] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [index, setIndex] = useState(null);

  useEffect(() => { api.get("/gallery/folders").then((r) => setFolders(r.data)).catch(() => setFolders([])); }, []);
  useEffect(() => {
    setMedia([]);
    if (!folderId) { return; }
    setLoading(true);
    api.get(`/gallery?project_id=${encodeURIComponent(folderId)}`).then((r) => setMedia(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [folderId]);

  const folder = (folders || []).find((f) => f.project_id === folderId);
  useSeo("gallery", folder ? { title: `${folder.project_name} Gallery – Photos & Videos | VG Homes`, image: folder.cover } : null);
  const open = (id) => { setParams(id ? { project: id } : {}); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div>
      <section className="bg-navy py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-teal mb-3">Media</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white" data-testid="gallery-title">{folderId ? (folder?.project_name || "Project Gallery") : "Gallery"}</h1>
          <p className="mt-4 text-white/70 max-w-xl">{folderId ? folder?.location : "Browse photos and videos from each of our developments."}</p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          {folderId ? (
            <>
              <button onClick={() => open(null)} data-testid="gallery-back" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-teal transition-colors">
                <ArrowLeft className="h-4 w-4" /> All project folders
              </button>
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-3" data-testid="gallery-media-grid">
                {media.map((m, i) => <Reveal key={m.id} delay={(i % 3) * 0.06}><MediaTile m={m} onOpen={() => setIndex(i)} /></Reveal>)}
              </div>
              {!loading && media.length === 0 && <p className="text-center text-slate-400 py-16" data-testid="gallery-empty">No media in this folder yet.</p>}
            </>
          ) : (
            <>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-folders">
                {(folders || []).map((f, i) => <Reveal key={f.project_id} delay={(i % 3) * 0.06}><FolderCard f={f} onOpen={() => open(f.project_id)} /></Reveal>)}
              </div>
              {folders && folders.length === 0 && <p className="text-center text-slate-400 py-16">No media yet.</p>}
            </>
          )}
        </div>
      </section>

      <Lightbox items={media} index={index} setIndex={setIndex} />
    </div>
  );
}
